import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Skeleton } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * Placeholder grid that mirrors `PurchaseSummaryView`'s layout while
 * the data is loading. Renders four card-shaped skeletons so the
 * layout doesn't shift when the real KPIs swap in.
 *
 * No drilled `id` / `data-testid` props — the widget's literal slot
 * prefix (`purchase-summary-loading`) is used directly so the testid
 * stays greppable in source. See user memory: "never drill id /
 * data-testid props into Views or Skeletons".
 */
export const PurchaseSummarySkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('purchases.widgets.purchaseSummary.title'),
        }),
        [t],
    );

    return (
        <section
            aria-busy
            aria-labelledby="purchase-summary-loading-title"
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('purchase-summary-loading')}
        >
            <h2
                className="text-base font-semibold text-foreground"
                {...getNodeTestIdProp('purchase-summary-loading-title')}
            >
                {copy.title}
            </h2>
            <div
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
                {...getNodeTestIdProp('purchase-summary-loading-grid')}
            >
                {Array.from({ length: 4 }).map((_, index) => (
                    <Card
                        className="flex flex-col gap-3 p-4"
                        key={`purchase-summary-loading-placeholder-${index}`}
                        {...getNodeTestIdProp(
                            `purchase-summary-loading-placeholder-${index}`,
                        )}
                    >
                        <div className="flex items-center gap-2">
                            <Skeleton className="size-8 rounded-md" />
                            <Skeleton className="h-3 w-24 rounded-md" />
                        </div>
                        <Skeleton className="h-7 w-28 rounded-md" />
                    </Card>
                ))}
            </div>
        </section>
    );
};
